// Shared empty-zone pruning for the nested zone trees
// (essSwitch / tgsSwitch / tesSwitch).
//
// Both the system pages (IntegratedSwitchLocations) and the global overview
// (IntegratedSystem) render the same slice data, so they must prune it the same
// way or their zone lists and unit counts drift apart.
//
// Tree shape produced by the *SwitchSlice reducers:
//   { [zoneId]: { isSpecificLocation: false, devices: { [mac]: device } } }
//   { [zoneId]: { isSpecificLocation: true, subLocations: { [subId]: { devices } } } }

// Returns the tree with any zone or sub-location that has no devices dropped
// entirely. Every device the endpoint returns is a real unit and is kept: a
// panel that has never reported carries fewer fields than one that has, but it
// is still configured hardware the location is expected to show.
export const filterSwitchTree = (switchTree) =>
  Object.fromEntries(
    Object.entries(switchTree || {})
      .map(([locationId, location]) => {
        if (!location?.isSpecificLocation) {
          if (!location?.devices) return null;

          if (Object.keys(location.devices).length > 0) {
            return [locationId, location];
          }

          return null;
        }

        const filteredSubLocations = Object.fromEntries(
          Object.entries(location.subLocations || {})
            .map(([subId, subLocation]) => {
              if (!subLocation?.devices) return null;

              if (Object.keys(subLocation.devices).length > 0) {
                return [subId, subLocation];
              }

              return null;
            })
            .filter(Boolean),
        );

        if (Object.keys(filteredSubLocations).length > 0) {
          return [
            locationId,
            { ...location, subLocations: filteredSubLocations },
          ];
        }

        return null;
      })
      .filter(Boolean),
  );

// Total number of units in a pruned tree, counting devices at the leaves.
export const countSwitchTreeDevices = (switchTree) =>
  Object.values(switchTree || {}).reduce((total, location) => {
    if (location?.isSpecificLocation) {
      return (
        total +
        Object.values(location.subLocations || {}).reduce(
          (subTotal, subLocation) =>
            subTotal + Object.keys(subLocation.devices || {}).length,
          0,
        )
      );
    }
    return total + Object.keys(location?.devices || {}).length;
  }, 0);
